import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {QUERIES} from '@github-ui/issue-url-helper/known-queries'
import {noop} from '@github-ui/noop'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLocation} from '@github-ui/react-router'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {createContext, type ReactNode, use, useCallback, useEffect, useMemo, useState} from 'react'
import type {PreloadFetchPolicy} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {VIEW_COLOR_FOREGROUND_MAP} from '../components/sidebar/ColorHelper'
import {CUSTOM_VIEW_ICONS_TO_PRIMER_ICON} from '../components/sidebar/IconHelper'
import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import useKnownViews from '../hooks/use-known-views'
import type {
  CreateDashboardSearchShortcutInput,
  createUserViewMutation$data,
} from '../mutations/__generated__/createUserViewMutation.graphql'
import type {
  SearchShortcutColor,
  SearchShortcutIcon,
  updateUserViewMutation$data,
} from '../mutations/__generated__/updateUserViewMutation.graphql'
import {commitCreateUserViewMutation} from '../mutations/create-user-view-mutation'
import {commitUpdateUserViewMutation} from '../mutations/update-user-view-mutation'
import type {AppPayload} from '../types/app-payload'
import {issueRegexpWithoutDomain, issuesPathRegexp} from '../utils/urls'

type ViewState = {
  viewName: string
  viewIcon: string
  viewDescription: string
  viewColor: string
  viewQuery: string
}

type QueryUpdateCallbacks = Partial<ViewState> & {
  viewId: string
  onSuccess?: (response: updateUserViewMutation$data) => void
  onError?: () => void
  relayEnvironment: Environment
}
type QueryCreateCallbacks = {
  onSuccess?: (response: createUserViewMutation$data) => void
  onError?: () => void
  relayEnvironment: Environment
}

type QueryDuplicateCallbacks = ViewState & {
  onSuccess?: (response: createUserViewMutation$data) => void
  onError?: () => void
  relayEnvironment: Environment
}

type QueryCreateCallbackInternal = {
  input: CreateDashboardSearchShortcutInput
  onSuccess?: (response: createUserViewMutation$data) => void
  onError?: () => void
  relayEnvironment: Environment
}

type QueryContextType = {
  // Core view navigation - changes on page navigation
  viewPosition: number | undefined
  setViewPosition: (q: number | undefined) => void
  isCustomView: (id: string) => boolean
  currentViewId: string
  setCurrentViewId: (viewId: string) => void
  savedViewsCount: number
  setSavedViewsCount: (count: number) => void
  saveViewsConnectionId: string | undefined
  setSaveViewsConnectionId: (q: string) => void
}

// Context for the active search query - changes when search is submitted
// Split into separate value and setter contexts for maximum re-render control
type QueryActiveSearchValueContextType = string
type QueryActiveSearchSetterContextType = (query: string) => void

// Context for pagination state - changes on page navigation
type QueryPaginationValueContextType = number | undefined
type QueryPaginationSetterContextType = (page: number | undefined) => void

// Context for query execution - rarely changes
type QueryExecutionValueContextType = ((query: string, fetchPolicy?: PreloadFetchPolicy) => void) | undefined
type QueryExecutionSetterContextType = (executeQuery: (query: string) => void) => void

// Context for loading state - changes during fetch operations
type QueryLoadingValueContextType = boolean
type QueryLoadingSetterContextType = (loading: boolean) => void

// Context for edit mode state - changes when entering/exiting edit mode
type QueryEditStateContextType = {
  isEditing: boolean
  setIsEditing: (editing: boolean) => void
  isNewView: boolean
  setIsNewView: (isNewView: boolean) => void
  canEditView: boolean
  setCanEditView: (canEditView: boolean) => void
  dirtyViewId: string | undefined
  setDirtyViewId: (viewId: string | undefined) => void
}

// Split into focused contexts to prevent unnecessary re-renders
// When dirtySearchQuery changes on every keystroke, only components that need it will re-render

// Context for search input state - changes on every keystroke
// Split into separate value and setter contexts for maximum re-render control
type QuerySearchInputValueContextType = string | null
type QuerySearchInputSetterContextType = (q: string | null) => void

// Context for debounced search query - changes every 500ms during typing
// Used by filter dropdowns that don't need keystroke-level updates
type QueryDebouncedSearchContextType = {
  debouncedDirtySearchQuery: string | null
}

// Context for view edit fields - changes during edit mode
type QueryViewEditFieldsContextType = {
  dirtyTitle: string | null
  setDirtyTitle: (title: string | null) => void
  dirtyDescription: string | null
  setDirtyDescription: (description: string | null) => void
  dirtyViewIcon: string | null
  setDirtyViewIcon: (icon: string | null) => void
  dirtyViewColor: string | null
  setDirtyViewColor: (color: string | null) => void
}

// Context for stable mutation callbacks - rarely changes
type QueryMutationsContextType = {
  commitUserViewCreate: ({onSuccess, onError, relayEnvironment}: QueryCreateCallbacks) => void
  commitUserViewDuplicate: ({
    onSuccess,
    onError,
    viewName,
    viewIcon,
    viewDescription,
    viewColor,
    viewQuery,
  }: QueryDuplicateCallbacks) => void
  commitUserViewEdit: ({viewId, onSuccess, onError, relayEnvironment}: QueryUpdateCallbacks) => void
  clearSavedViewEditState: () => void
}

// Context for bulk operations - rarely changes
type QueryBulkContextType = {
  bulkJobId: string | null
  setBulkJobId: (bulkJobId: string | null) => void
}

// Context for focus behavior - shared between SearchBar and HeaderContent
type QueryFocusContextType = {
  shouldFocusSearchOnNav: boolean
  setShouldFocusSearchOnNav: (shouldFocus: boolean) => void
}

type QueryContextProviderType = {
  children: ReactNode
}

const QueryContext = createContext<QueryContextType>({
  viewPosition: undefined,
  setViewPosition: noop,
  isCustomView: (id: string) => id === '',
  currentViewId: VIEW_IDS.empty,
  setCurrentViewId: noop,
  savedViewsCount: 0,
  setSavedViewsCount: noop,
  saveViewsConnectionId: undefined,
  setSaveViewsConnectionId: noop,
})

// Split contexts for active search query - separate value and setter for maximum re-render control
const QueryActiveSearchValueContext = createContext<QueryActiveSearchValueContextType>('')
const QueryActiveSearchSetterContext = createContext<QueryActiveSearchSetterContextType>(noop)

// Split context for pagination - separate value and setter
const QueryPaginationValueContext = createContext<QueryPaginationValueContextType>(undefined)
const QueryPaginationSetterContext = createContext<QueryPaginationSetterContextType>(noop)

// Split context for query execution - separate value and setter
const QueryExecutionValueContext = createContext<QueryExecutionValueContextType>(undefined)
const QueryExecutionSetterContext = createContext<QueryExecutionSetterContextType>(noop)

// Split context for loading state - separate value and setter
const QueryLoadingValueContext = createContext<QueryLoadingValueContextType>(false)
const QueryLoadingSetterContext = createContext<QueryLoadingSetterContextType>(noop)

const QueryEditStateContext = createContext<QueryEditStateContextType>({
  isEditing: false,
  setIsEditing: noop,
  isNewView: false,
  setIsNewView: noop,
  canEditView: false,
  setCanEditView: noop,
  dirtyViewId: undefined,
  setDirtyViewId: noop,
})

// Split contexts for better performance - each context only triggers re-renders for its consumers
// Components that only need the setter (like filter buttons) won't re-render on every keystroke
const QuerySearchInputValueContext = createContext<QuerySearchInputValueContextType>(null)
const QuerySearchInputSetterContext = createContext<QuerySearchInputSetterContextType>(noop)

const QueryDebouncedSearchContext = createContext<QueryDebouncedSearchContextType>({
  debouncedDirtySearchQuery: null,
})

const QueryViewEditFieldsContext = createContext<QueryViewEditFieldsContextType>({
  dirtyTitle: '',
  setDirtyTitle: noop,
  dirtyDescription: '',
  setDirtyDescription: noop,
  dirtyViewIcon: null,
  setDirtyViewIcon: noop,
  dirtyViewColor: null,
  setDirtyViewColor: noop,
})

const QueryMutationsContext = createContext<QueryMutationsContextType>({
  commitUserViewCreate: noop,
  commitUserViewEdit: noop,
  commitUserViewDuplicate: noop,
  clearSavedViewEditState: noop,
})

const QueryBulkContext = createContext<QueryBulkContextType>({
  bulkJobId: null,
  setBulkJobId: noop,
})

const QueryFocusContext = createContext<QueryFocusContextType>({
  shouldFocusSearchOnNav: false,
  setShouldFocusSearchOnNav: noop,
})

// This is a deprecated method that we used when we implemented CustomViews on issues dashboard
// The method should return VIEW_IDS.repository when we're on the repo level in any of new/choose/show/index actions
// It should otherwise return the id of the view that you're on - when coming from the dashboard page
const useInitialViewId = () => {
  const {scoped_repository} = useAppPayload<AppPayload>()
  const {pathname} = useLocation()
  if (pathname.match(issueRegexpWithoutDomain)) {
    return scoped_repository ? VIEW_IDS.repository : VIEW_IDS.assignedToMe
  }
  const id = pathname.split('/').pop() || ''

  if (scoped_repository && pathname.match(issuesPathRegexp)) {
    return VIEW_IDS.repository
  }

  return id === 'issues' || !id ? VIEW_IDS.empty : id
}

export function QueryContextProvider({children}: QueryContextProviderType) {
  const {
    initial_view_content: {can_edit_view: initialCanEditView},
  } = useAppPayload<AppPayload>()

  const {addToast} = useToastContext()
  const {search} = useLocation()
  const urlSearchParams = new URLSearchParams(search)
  const queryParam = urlSearchParams.get('q')
  const urlQuery = !queryParam || queryParam === '' ? QUERIES.defaultRepoLevelOpen : queryParam

  const [canEditView, setCanEditView] = useState(initialCanEditView)
  const [dirtyViewQuery, setDirtyViewQuery] = useState<string | null>(null)
  const [activeSearchQuery, setActiveSearchQuery] = useState<string>(urlQuery)
  const [viewPosition, setViewPosition] = useState<number | undefined>(undefined)
  const [saveViewsConnectionId, setSaveViewsConnectionId] = useState<string | undefined>(undefined)
  const [dirtyTitle, setDirtyTitle] = useState<string | null>(null)
  const [dirtyDescription, setDirtyDescription] = useState<string | null>(null)
  const [dirtyViewIcon, setDirtyViewIcon] = useState<string | null>(null)
  const [dirtyViewColor, setDirtyViewColor] = useState<string | null>(null)
  const [dirtyViewId, setDirtyViewId] = useState<string | undefined>(undefined)
  const initialViewId = useInitialViewId()
  const [currentViewId, setCurrentViewId] = useState<string>(initialViewId)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isQueryLoading, setIsQueryLoading] = useState<boolean>(false)
  const [isNewView, setIsNewView] = useState<boolean>(false)
  const {knownViews} = useKnownViews()
  const [currentPage, setCurrentPage] = useState<number | undefined>()
  const [executeQuery, setExecuteQuery] = useState<((query: string) => void) | undefined>()
  const [savedViewsCount, setSavedViewsCount] = useState(0)
  const [debouncedDirtySearchQuery, setDebouncedDirtySearchQuery] = useState<string | null>(null)
  const [shouldFocusSearchOnNav, setShouldFocusSearchOnNav] = useState(false)

  // To enable 'syncing' of the list pickers selected value and the _not yet submitted_ dirty search query
  // we need to debounce the dirty search query. This is because the list pickers attempt to fetch their data
  // when a pre-selected value is present and we don't want to trigger on every keystroke.
  const debouncedSetDebouncedDirtySearchQuery = useDebounce(setDebouncedDirtySearchQuery, 500)

  useEffect(() => {
    debouncedSetDebouncedDirtySearchQuery(dirtyViewQuery)
  }, [debouncedSetDebouncedDirtySearchQuery, dirtyViewQuery])

  const [bulkJobId, setBulkJobId] = useLocalStorage<string | null>(VALUES.localStorageKeyBulkUpdateIssues, null)

  const isCustomView = useCallback(
    (viewId: string | undefined) => {
      if (!viewId) return false
      return knownViews.findIndex(s => s.id === viewId) === -1
    },
    [knownViews],
  )

  const clearSavedViewEditState = useCallback(() => {
    setDirtyViewQuery(null)
    setDirtyTitle(null)
    setDirtyDescription(null)
    setDirtyViewIcon(null)
    setDirtyViewColor(null)
    setDirtyViewId(undefined)
    setShouldFocusSearchOnNav(false)
  }, [])

  const commitUserViewEdit = useCallback(
    ({
      viewId,
      viewName,
      viewIcon,
      viewColor,
      viewDescription,
      viewQuery,
      onSuccess,
      onError,
      relayEnvironment,
    }: QueryUpdateCallbacks) => {
      if (!isCustomView(viewId)) return

      commitUpdateUserViewMutation({
        environment: relayEnvironment,
        input: {
          shortcutId: viewId,
          query: viewQuery,
          name: viewName,
          description: viewDescription,
          icon: viewIcon as SearchShortcutIcon,
          color: viewColor as SearchShortcutColor,
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: LABELS.views.updateError,
          })
          onError?.()
        },
        onCompleted: (response: updateUserViewMutation$data) => {
          onSuccess?.(response)
        },
      })
    },
    [isCustomView, addToast],
  )

  const internalCommitViewCreate = useCallback(
    ({input, onSuccess, onError, relayEnvironment}: QueryCreateCallbackInternal) => {
      input.name = input.name || LABELS.views.defaultName
      input.query = input.query === null || input.query === undefined ? dirtyViewQuery : input.query

      return commitCreateUserViewMutation({
        environment: relayEnvironment,
        input,
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: LABELS.views.createError,
          })
          onError?.()
        },
        onCompleted: response => {
          onSuccess?.(response)
        },
      })
    },
    [dirtyViewQuery, addToast],
  )

  const commitUserViewCreate = useCallback(
    ({onSuccess, onError, relayEnvironment}: QueryCreateCallbacks) => {
      return internalCommitViewCreate({
        input: {
          query: VALUES.defaultQueryForNewView,
          name: LABELS.views.defaultName,
          searchType: 'ISSUES',
          icon: VALUES.defaultViewIcon as SearchShortcutIcon,
          color: VALUES.defaultViewColor as SearchShortcutColor,
        },
        onSuccess,
        onError,
        relayEnvironment,
      })
    },
    [internalCommitViewCreate],
  )

  const commitUserViewDuplicate = useCallback(
    ({
      onSuccess,
      onError,
      viewName,
      viewIcon,
      viewColor,
      viewDescription,
      viewQuery,
      relayEnvironment,
    }: QueryDuplicateCallbacks) => {
      return internalCommitViewCreate({
        input: {
          query: viewQuery,
          name: `${viewName} copy`,
          description: viewDescription,
          color: (VIEW_COLOR_FOREGROUND_MAP[viewColor] ? viewColor : VALUES.defaultViewColor) as SearchShortcutColor,
          icon: (CUSTOM_VIEW_ICONS_TO_PRIMER_ICON[viewIcon] ? viewIcon : VALUES.defaultViewIcon) as SearchShortcutIcon,
          searchType: 'ISSUES',
        },
        onSuccess,
        onError,
        relayEnvironment,
      })
    },
    [internalCommitViewCreate],
  )

  const queryContextValue = useMemo<QueryContextType>(() => {
    return {
      viewPosition,
      setViewPosition,
      isCustomView,
      currentViewId,
      setCurrentViewId,
      savedViewsCount,
      setSavedViewsCount,
      saveViewsConnectionId,
      setSaveViewsConnectionId,
    }
  }, [viewPosition, isCustomView, currentViewId, savedViewsCount, saveViewsConnectionId])

  // Split into focused contexts for better performance
  // No useMemo needed for primitive values - React handles them efficiently
  // Setters are stable from useState so no useMemo needed

  const queryEditStateValue = useMemo<QueryEditStateContextType>(
    () => ({
      isEditing,
      setIsEditing,
      isNewView,
      setIsNewView,
      canEditView,
      setCanEditView,
      dirtyViewId,
      setDirtyViewId,
    }),
    [isEditing, isNewView, canEditView, dirtyViewId],
  )

  // Split context values - each updates independently to minimize re-renders
  // No useMemo needed for primitive values - React handles them efficiently
  // Setters are stable from useState so no useMemo needed

  const queryDebouncedSearchValue = useMemo<QueryDebouncedSearchContextType>(
    () => ({
      debouncedDirtySearchQuery,
    }),
    [debouncedDirtySearchQuery],
  )

  const queryViewEditFieldsValue = useMemo<QueryViewEditFieldsContextType>(
    () => ({
      dirtyTitle,
      setDirtyTitle,
      dirtyDescription,
      setDirtyDescription,
      dirtyViewIcon,
      setDirtyViewIcon,
      dirtyViewColor,
      setDirtyViewColor,
    }),
    [dirtyTitle, dirtyDescription, dirtyViewIcon, dirtyViewColor],
  )

  const queryMutationsValue = useMemo<QueryMutationsContextType>(
    () => ({
      commitUserViewCreate,
      commitUserViewDuplicate,
      commitUserViewEdit,
      clearSavedViewEditState,
    }),
    [commitUserViewCreate, commitUserViewDuplicate, commitUserViewEdit, clearSavedViewEditState],
  )

  const queryBulkValue = useMemo<QueryBulkContextType>(
    () => ({
      bulkJobId,
      setBulkJobId,
    }),
    [bulkJobId, setBulkJobId],
  )

  const queryFocusValue = useMemo<QueryFocusContextType>(
    () => ({
      shouldFocusSearchOnNav,
      setShouldFocusSearchOnNav,
    }),
    [shouldFocusSearchOnNav],
  )

  return (
    <QueryContext value={queryContextValue}>
      <QueryActiveSearchValueContext value={activeSearchQuery}>
        <QueryActiveSearchSetterContext value={setActiveSearchQuery}>
          <QueryPaginationValueContext value={currentPage}>
            <QueryPaginationSetterContext value={setCurrentPage}>
              <QueryExecutionValueContext value={executeQuery}>
                <QueryExecutionSetterContext value={setExecuteQuery}>
                  <QueryLoadingValueContext value={isQueryLoading}>
                    <QueryLoadingSetterContext value={setIsQueryLoading}>
                      <QueryEditStateContext value={queryEditStateValue}>
                        <QuerySearchInputValueContext value={dirtyViewQuery}>
                          <QuerySearchInputSetterContext value={setDirtyViewQuery}>
                            <QueryDebouncedSearchContext value={queryDebouncedSearchValue}>
                              <QueryViewEditFieldsContext value={queryViewEditFieldsValue}>
                                <QueryMutationsContext value={queryMutationsValue}>
                                  <QueryBulkContext value={queryBulkValue}>
                                    <QueryFocusContext value={queryFocusValue}>{children}</QueryFocusContext>
                                  </QueryBulkContext>
                                </QueryMutationsContext>
                              </QueryViewEditFieldsContext>
                            </QueryDebouncedSearchContext>
                          </QuerySearchInputSetterContext>
                        </QuerySearchInputValueContext>
                      </QueryEditStateContext>
                    </QueryLoadingSetterContext>
                  </QueryLoadingValueContext>
                </QueryExecutionSetterContext>
              </QueryExecutionValueContext>
            </QueryPaginationSetterContext>
          </QueryPaginationValueContext>
        </QueryActiveSearchSetterContext>
      </QueryActiveSearchValueContext>
    </QueryContext>
  )
}

export function useQueryContext() {
  return use(QueryContext)
}

// Focused hooks for active search query - separate getter and setter for maximum re-render control
// Components using only the setter (like buttons) won't re-render when the value changes
export function useActiveSearchQuery() {
  return use(QueryActiveSearchValueContext)
}

export function useSetActiveSearchQuery() {
  return use(QueryActiveSearchSetterContext)
}

// Focused hooks for pagination state - separate getter and setter
export function useCurrentPage() {
  return use(QueryPaginationValueContext)
}

export function useSetCurrentPage() {
  return use(QueryPaginationSetterContext)
}

// Focused hooks for query execution - separate getter and setter
export function useExecuteQuery() {
  return use(QueryExecutionValueContext)
}

export function useSetExecuteQuery() {
  return use(QueryExecutionSetterContext)
}

// Focused hooks for loading state - separate getter and setter
export function useIsQueryLoading() {
  return use(QueryLoadingValueContext)
}

export function useSetIsQueryLoading() {
  return use(QueryLoadingSetterContext)
}

export function useQueryEditState() {
  return use(QueryEditStateContext)
}

// Split hooks for focused context consumption - use these for better performance
// Separate getter and setter hooks for search input - keystroke-level updates
export function useDirtySearchQuery() {
  return use(QuerySearchInputValueContext)
}

export function useSetDirtySearchQuery() {
  return use(QuerySearchInputSetterContext)
}

export function useQueryViewEditFields() {
  return use(QueryViewEditFieldsContext)
}

export function useQueryMutations() {
  return use(QueryMutationsContext)
}

export function useQueryBulk() {
  return use(QueryBulkContext)
}

export function useQueryFocus() {
  return use(QueryFocusContext)
}

export function useQueryDebouncedSearch() {
  return use(QueryDebouncedSearchContext)
}
